<!--
  The slides of the account pop-up of a mentor chat card, between "Your
  request is written" and the sign-up page. They show, not tell, what
  GrowthMentor is: people who talk to other people.
  - 2: real calls. A cut of the "You work alone. Don't think alone." film
    (four mentors on real calls, captions in the picture), muted until the
    person turns the sound on.
  - 3: unlimited calls, with the mentors of this person's own chat (from a
    mentor profile or a Browse card: that mentor and two empty slots).
  - 4: a wall of mentor faces, with the words of a member who talked to
    more than 15 of them.
-->
<template>
  <!-- Real calls. -->
  <div v-if="step === 2" class="w-full" test_id="chat-slide-film">
    <div
      class="relative -mx-6 -mt-6 overflow-hidden rounded-t-2xl bg-neutral-900"
    >
      <video
        ref="film"
        class="aspect-[4/5] max-h-[52vh] w-full object-cover object-bottom"
        src="/videos/dont-think-alone.mp4"
        poster="/videos/dont-think-alone-poster.jpg"
        autoplay
        muted
        loop
        playsinline
        preload="auto"
      />
      <button
        type="button"
        class="absolute right-3 bottom-3 inline-flex items-center gap-1.5 rounded-full bg-black/55 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur hover:bg-black/70"
        @click="toggleSound"
      >
        <VolumeIcon v-if="!soundOn" class="size-4" />
        <VolumeOffIcon v-else class="size-4" />
        {{ soundOn ? 'Sound off' : 'Sound on' }}
      </button>
    </div>
    <p
      class="mt-5 text-center text-2xl leading-tight font-bold tracking-tight text-neutral-900"
    >
      You work alone.
      <span class="text-secondary-600 block">Don't think alone.</span>
    </p>
    <p class="mt-2 text-center text-sm text-neutral-500">
      Real calls between members and mentors on GrowthMentor.
    </p>
  </div>

  <!-- Unlimited calls, with the mentors of this chat. -->
  <div v-else-if="step === 3" class="w-full" test_id="chat-slide-unlimited">
    <div class="flex flex-wrap items-center gap-3">
      <InfinityIcon class="text-secondary-500 size-10" />
      <div
        class="bg-secondary-50 flex items-center gap-2 rounded-full py-1.5 pr-3.5 pl-1.5"
      >
        <div class="flex items-center">
          <img
            v-for="(face, i) in MENTOR_FACES.slice(0, 3)"
            :key="face"
            :src="face"
            alt=""
            class="ring-secondary-50 size-6 rounded-full object-cover ring-2"
            :class="i > 0 ? '-ml-2' : ''"
          />
        </div>
        <span class="text-secondary-700 text-[13px] font-bold">
          {{ totalMentors }}+ vetted mentors
        </span>
      </div>
    </div>
    <p class="mt-4 text-xl font-semibold text-neutral-900">
      Your membership includes unlimited calls.
    </p>
    <p class="mt-1 text-sm leading-relaxed text-neutral-600">
      No single person is an expert at everything. Talk to the right mentor for
      each part of your problem.
    </p>
    <ul class="mt-4 space-y-2">
      <li
        v-for="(mentor, index) in mentors"
        :key="mentor.id"
        class="flex items-center gap-3 rounded-2xl p-2.5"
        :class="
          index === 0
            ? 'border border-neutral-200 bg-white shadow-sm'
            : 'border-2 border-dashed border-neutral-200'
        "
      >
        <Avatar :src="mentor.avatarUrl ?? ''" size="md" class="shrink-0" />
        <div class="min-w-0 flex-1">
          <p class="truncate text-sm font-semibold text-neutral-900">
            {{ mentor.name }}
          </p>
          <p v-if="mentor.title" class="truncate text-xs text-neutral-500">
            {{ mentor.title }}
          </p>
        </div>
        <span
          v-if="index === 0"
          class="inline-flex shrink-0 items-center gap-2 text-xs font-medium text-neutral-700"
        >
          <span
            class="bg-secondary-500 size-2 rounded-full"
            aria-hidden="true"
          />
          {{ firstCall ? 'Your first call' : 'Request ready' }}
        </span>
        <span v-else class="shrink-0 text-xs text-neutral-500">Free slot</span>
      </li>
      <!-- A profile or a Browse card has one mentor: the empty slots of the
           wizard's "Did you know?" show what unlimited means. -->
      <template v-if="firstCall">
        <li
          v-for="slot in 2"
          :key="`slot-${slot}`"
          class="rounded-2xl border-2 border-dashed border-neutral-200 p-3.5 text-center text-sm font-medium text-neutral-500"
        >
          Free session slot available
        </li>
      </template>
    </ul>
  </div>

  <!-- A wall of faces, and a member's own words. -->
  <div v-else class="w-full" test_id="chat-slide-faces">
    <div class="relative -mx-6 -mt-6 overflow-hidden rounded-t-2xl">
      <div class="grid grid-cols-8 gap-1 p-1" aria-hidden="true">
        <img
          v-for="(face, i) in MENTOR_FACES"
          :key="face"
          :src="face"
          alt=""
          class="face aspect-square w-full rounded-md object-cover"
          :style="{ animationDelay: `${(i * 37) % 900}ms` }"
        />
      </div>
      <div
        class="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/0 via-white/10 to-white"
      />
      <figure
        class="quote absolute inset-x-6 bottom-6 rounded-2xl bg-white p-4 shadow-[0_20px_45px_-15px_rgba(20,31,47,0.45)]"
      >
        <blockquote
          class="text-[15px] leading-snug font-medium text-neutral-900"
        >
          “I can’t believe I had the chance to talk to over 15 Marketers and
          Founders already!”
        </blockquote>
        <figcaption class="mt-3 flex items-center gap-2.5">
          <img
            src="/social-proof/maria-ledentsova-avatar.webp"
            alt=""
            class="size-8 rounded-full object-cover"
          />
          <span class="text-xs text-neutral-500">
            <b class="font-semibold text-neutral-800">Maria Ledentsova,</b>
            member, on LinkedIn
          </span>
        </figcaption>
      </figure>
    </div>
    <p
      class="mt-5 text-center text-2xl leading-tight font-bold tracking-tight text-neutral-900"
    >
      Talk to one. Then another.
      <span class="text-secondary-600 block">Then another.</span>
    </p>
    <p class="mt-2 text-center text-sm text-neutral-500">
      {{ totalMentors }}+ mentors. One membership.
    </p>
  </div>
</template>

<script setup lang="ts">
import { ref, useTemplateRef } from 'vue'
import InfinityIcon from '~icons/lucide/infinity'
import VolumeIcon from '~icons/lucide/volume-2'
import VolumeOffIcon from '~icons/lucide/volume-x'
import Avatar from '@/shared/components/avatar/Avatar.vue'
import { MENTOR_FACES } from '@/modules/mentor-chat/mentorFaces'

defineProps<{
  step: number
  totalMentors: number
  // From a mentor profile or a Browse card: one mentor, their first call,
  // and empty slots under it.
  firstCall?: boolean
  // The mentors of the chat row, the clicked one first.
  mentors: {
    id: number
    name: string
    title: string | null
    avatarUrl: string | null
  }[]
}>()

// The film plays muted; the button turns the sound on from the start.
const film = useTemplateRef<HTMLVideoElement>('film')
const soundOn = ref(false)
const toggleSound = () => {
  const video = film.value
  if (!video) return
  soundOn.value = !soundOn.value
  video.muted = !soundOn.value
  if (soundOn.value) video.currentTime = 0
  video.play().catch(() => undefined)
}
</script>

<style scoped>
.face {
  animation: face-in 0.5s cubic-bezier(0.22, 1, 0.36, 1) backwards;
}

.quote {
  animation: quote-in 0.6s cubic-bezier(0.22, 1, 0.36, 1) 0.9s backwards;
}

@keyframes face-in {
  from {
    opacity: 0;
    transform: scale(0.6);
  }
}

@keyframes quote-in {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .face,
  .quote {
    animation: none;
  }
}
</style>
